import { LOCAL_STORAGE_KEY_USER_FILTERS } from '@/helpers/constants'
import { useCurrentCompany } from '@/hooks/swr/useCurrentCompany'
import { useRouteParams } from '@/hooks/useRouteParams'
import { computed, ref, watch } from 'vue'

export type Nullable<T> = null | T

type NullableBoolean = Nullable<boolean>

type SortOption =
  | 'last_name'
  | 'phone_number'
  | 'position'
  | 'department'
  | 'email'

type OrderOption = 'asc' | 'desc'

export type FilterParams = {
  active: NullableBoolean
  report: NullableBoolean
  temp: NullableBoolean
  anonymous: NullableBoolean
  department: Nullable<string>
  position: Nullable<string>
  query: string
  sort_by?: SortOption
  order_by?: OrderOption
  with_subdepartments: boolean
  page: number
  per_page: number
}

const DEFAULT_FILTER_PARAMS: FilterParams = {
  active: true,
  report: null,
  temp: null,
  anonymous: null,
  department: null,
  position: null,
  query: '',
  sort_by: 'last_name',
  order_by: 'asc',
  with_subdepartments: true,
  page: 1,
  per_page: 25,
}

export const useUserFilter = () => {
  const { router } = useRouteParams()

  const { data: company } = useCurrentCompany()

  const companyNameKey = computed(() => company.value?.name_key)

  const filterParams = ref<FilterParams>(DEFAULT_FILTER_PARAMS)

  const updateFilterParams = (newFilterParams: Partial<FilterParams>) => {
    if (!('page' in newFilterParams)) {
      filterParams.value.page = 1
    }
    const params = { ...filterParams.value, ...newFilterParams }
    for (const key of Object.keys(newFilterParams) as Array<
      keyof FilterParams
    >) {
      if (newFilterParams[key] === undefined) delete params[key]
    }
    filterParams.value = params
    saveFilterParams()
  }

  const saveFilterParams = () => {
    const storageKey = getStorageKeyForRoute()

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        ...filterParams.value,
        name_key: companyNameKey.value,
      })
    )
  }

  const getStorageKeyForRoute = () => {
    const routeName = router.currentRoute.name
    const lowerCaseRouteName = routeName.toLowerCase()
    const storageKeyElements = lowerCaseRouteName.split('_').slice(2)
    const storageKey = storageKeyElements.join('_')
    return `${LOCAL_STORAGE_KEY_USER_FILTERS}:${storageKey}`
  }

  const resetFilterParams = () => {
    filterParams.value = DEFAULT_FILTER_PARAMS
    saveFilterParams()
  }

  const setSavedFilters = (storedFilterParams: string) => {
    const { name_key, ...storedParams } = JSON.parse(storedFilterParams)

    if (companyNameKey.value && companyNameKey.value !== name_key) {
      resetFilterParams()
      return
    }

    filterParams.value = storedParams
  }

  const getStoredFilterParams = () => {
    const storageKey = getStorageKeyForRoute()

    const savedFilterParams = localStorage.getItem(storageKey)

    if (savedFilterParams) {
      setSavedFilters(savedFilterParams)
    }
  }

  watch(
    company,
    () => {
      if (company.value) getStoredFilterParams()
    },
    {
      deep: true,
      immediate: true,
    }
  )

  return { filterParams, updateFilterParams, resetFilterParams }
}
